import { Dispatch } from "redux";
import { DRIVE_TAB } from "@/utils/constants/tabConstants";
import { FileItem } from "./upload.models";
import { RootState } from "../index";
import {
  addFile,
  decreaseCounter,
  increaseCounter,
  setUploadingError,
} from "@/store/upload/upload.slice";
import {
  denMarkArticleGetApi,
  denmarkDetailApi,
  RegisterCustomMediaOnCMSApi,
  RegisterMediaOnCMSApi,
} from "@/database";
import { AppDispatch } from "../index";
import { stripExtension } from "@/utils/ui/uiFunctions";

export const callRegisterMediaOnCms =
  (params: any, callback: (response: any) => void) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const {
      file,
      size,
      uid,
      isShorts,
      type,
      id,
      cdn,
      name,
      damid,
      asset_type,
      data,
      asset_id,
      subtitle,
      language,
      enabled,
      media,
      business,
      aspect_ratio,
      orientation,
    } = params;

    const identifier =
      params.uniqueIdentifier ||
      file.uniqueIdentifier ||
      `${size}-${file.name.replace(/[.\s]/g, "")}`;
    const mediaName = stripExtension(file.name);
    // Thumbnails/posters (name/type "thumb") register alongside the main
    // media upload but must stay out of the shared upload-progress tracker.
    const shouldTrackProgress = name !== "thumb" && type !== "thumb";

    if (shouldTrackProgress) {
      dispatch(
        addFile({
          key: identifier,
          file: {
            id: "",
            name: file.name,
            size,
            asset_type,
            identifier,
            title: mediaName,
            filename: file.name,
            file_id: params.file_id,
            at: "video",
            slikeId: "",
            progress: 0,
            uploadPhase: "preparing",
            isUploadedSuccessfully: false,
            resumableIdentifier: identifier,
            error: null,
          },
        })
      );
      dispatch(increaseCounter());
    }

    try {
      let method = "media.register";
      if (name !== "media") method = "assets.register";
      if (name == "subtitle") method = "subtitles.register";

      const dataTitle = data?.title ? stripExtension(data.title) : undefined;

      const response = await RegisterMediaOnCMSApi(
        {
          fid: size + file.name,
          title: dataTitle || mediaName,
          source: "cms",
          name: name === "subtitle" ? language : name,
          asset_type: asset_type === DRIVE_TAB ? "video" : asset_type,
          content_type: asset_type === DRIVE_TAB ? 2 : undefined,
          type: type,
          isShorts: isShorts,
          is_short: isShorts ? 1 : 0,
          asset_id: asset_id,
          id: id,
          cdn: cdn,
          media: media,
          ctype: asset_type === "campaign" ? "Campaign" : "",
          damid: damid,
          stitle: [dataTitle || mediaName],
          meta_config: data?.meta_config,
          subtitle: subtitle,
          language: language,
          enabled: enabled,
          desc: data?.desc || "",
          default: false,
          business: business,
          preset_config:
            params.data?.preset_config ||
            file?.preset_config ||
            params.preset_config,
          ...(aspect_ratio && { aspect_ratio }),
          ...(orientation && { orientation }),
        },
        method
      );

      if (!response.data || response.status !== 200) {
        if (shouldTrackProgress) {
          dispatch(
            setUploadingError({
              uniqueIdentifier: identifier,
              error: `Error registering media: ${file.name}`,
            })
          );
          dispatch(decreaseCounter());
        }
        callback && callback(response);
        return;
      }

      // The file may have been replaced while registration was in flight.
      // Its uploader has already emitted an error and removed it from the
      // active queue, so do not resurrect a stale progress entry or callback.
      if (
        shouldTrackProgress &&
        getState().upload.files[identifier]?.file.error
      ) {
        return;
      }

      if (shouldTrackProgress) {
        const newFile: FileItem = {
          id: response.data.id,
          name: file.name,
          size,
          asset_type: asset_type,
          identifier: identifier,
          title: stripExtension(file.name),
          filename: file.name,
          file_id: params.file_id,
          at: "video",
          slikeId: response.data.id,
          progress: 0,
          uploadPhase: "uploading",
          isUploadedSuccessfully: false,
          resumableIdentifier: identifier,
          error: null,
        };

        dispatch(
          addFile({
            key: identifier,
            file: newFile,
          })
        );
      }

      callback && callback(response);

      //   const mediaResponse = await GetMediaApi(token, { id: response.data.id });
      // if (mediaResponse && mediaResponse.status === 200) {
      //   dispatch(addMediaInMediaList({ media: mediaResponse.data }));
      // }
    } catch (error) {
      console.error("Error registering media:", error);
      const message = `Error processing file: ${file.name}`;
      if (shouldTrackProgress) {
        dispatch(
          setUploadingError({
            uniqueIdentifier: identifier,
            error: message,
          })
        );
        dispatch(decreaseCounter());
      }
      if (callback) callback({ status: 500, error: message });
    }
  };

//Have to update Upload State and add this media in MediaList
export const callRegisterCustomMediaOnCms =
  (params: any, callback: (response: any) => void) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    try {
      const {
        type,
        data,
        name,
        title,
        source,
        embed,
        metaa,
        islive,
        asset_type,
        descriptions,
        tags,
        meta_config,
        id,
        thumb,
        thumbId,
        preset_config,
        fid,
        media_schedule,
        playback_mode,
        loop_count,
        is_borrow,
        ctype,
        team,
        isShorts,
        is_short,
      } = params;

      const resolvedIsShort =
        is_short != null && is_short !== ""
          ? is_short
          : isShorts != null
            ? isShorts
              ? 1
              : 0
            : asset_type === "shorts"
              ? 1
              : 0;
      const { description, downloads, ...restData } = data ?? {};

      let { meta } = restData;
      meta = { ...meta, ...metaa };
      const apiPayload = {
        name: name,
        id: id,
        type: type,
        source: source !== "" ? source : "cms",
        islive: islive ?? 0,
        title: title?.split(".")[0],
        preset_config: preset_config,
        meta_config: meta_config,
        fid: fid || "",
        ...(team && { team }),
        desc: descriptions ? descriptions : description ? description : "",
        ...(type === "youtube_download" && {
          tags: tags ?? [],
          desc: descriptions ?? "",
        }),
        asset_type: asset_type,
        is_short: resolvedIsShort,
        isShorts: resolvedIsShort === 1,
        ...(type === "youtube" && { youtube_id: restData.id }),
        ...(type === "simulive"
          ? { simulive: data }
          : type === "premiere"
            ? {}
            : source === "embed"
              ? { ...restData, tags: data.tags }
              : { downloads: data }),
        ...(embed && { embed }),
        ...(meta && { meta }),
        ...(source === "embed"
          ? { downloads: downloads }
          : { downloads: params.downloads }),
        ...(type === "podcast" && {
          tags: tags ?? [],
          stitle: [title.split(".")[0]],
          thumb: thumb,
          thumbId: thumbId,
        }),
        ...(type === "premiere" && {
          ...(media_schedule != null && { media_schedule }),
          ...(playback_mode != null &&
            playback_mode !== "" && { playback_mode }),
          ...(loop_count != null && { loop_count }),
          ctype: ctype,
          is_borrow: Boolean(is_borrow),
        }),
      };

      const response = await RegisterCustomMediaOnCMSApi(apiPayload);
      if (response) {
        callback && callback(response);
      }
    } catch (error) {
      console.error("API call failed:", error);
    }
  };

export const callRegisterPosterAssetOnCms =
  (params: any, callback: (response: any) => void) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    try {
      const { file, mediaId, cdn, damid, business } = params;
      const title = file.file?.name ? stripExtension(file.file.name) : mediaId;
      const response = await RegisterMediaOnCMSApi(
        {
          fid: `${mediaId}${file.file?.name || ".png"}`,
          title,
          source: "cms",
          name: "thumb",
          type: "thumb",
          isShorts: false,
          is_short: 0,
          id: mediaId,
          cdn: cdn,
          ctype: "",
          damid: damid,
          stitle: [title],
          desc: "",
          default: false,
          business: business,
        },
        "assets.register"
      );
      callback && callback(response);
    } catch (error) {
      console.error("assets.register failed:", error);
    }
  };

export const callDenmarkDetailApi =
  (params: any, callback: (response: any) => void) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const response = await denmarkDetailApi(
      params.detailId,
      params.hostId,
      params.isall
    );
    if (response) {
      callback && callback(response);
    }
  };

export const callDenmarkArticleGetApi =
  (params: any, callback: (response: any) => void) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const response = await denMarkArticleGetApi(params.detailId, params.hostId);
    if (response) {
      callback && callback(response);
    }
  };
